import React from 'react';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaUsers, FaMicrophone, FaPaintBrush, FaRobot, FaGlobeAsia } from 'react-icons/fa';
import { GiTheater, GiChemicalTank, GiSoccerBall } from 'react-icons/gi';
import { MdScience, MdComputer, MdBusinessCenter } from 'react-icons/md';
import './activities.css';

const Activities = () => {
  const activityCategories = [
    {
      id: 1,
      title: "Academic Clubs",
      description: "Subject-focused organizations that enhance classroom learning",
      icon: <MdComputer size={40} />,
      activities: ["Computer Science Society", "Engineering Innovators", "Math Olympiad Team"]
    },
    {
      id: 2,
      title: "Cultural Organizations",
      description: "Celebrating diversity through arts and traditions",
      icon: <GiTheater size={40} />,
      activities: ["International Students Association", "Traditional Dance Troupe", "Multicultural Festival Committee"]
    },
    {
      id: 3,
      title: "Performing Arts",
      description: "Opportunities for creative expression and performance",
      icon: <FaMicrophone size={40} />,
      activities: ["Choir & Orchestra", "Drama Club", "Stand-up Comedy Society"]
    },
    {
      id: 4,
      title: "Sports & Recreation",
      description: "Competitive teams and recreational activities",
      icon: <GiSoccerBall size={40} />,
      activities: ["Varsity Teams", "Intramural Leagues", "Adventure Club"]
    },
    {
      id: 5,
      title: "Visual Arts",
      description: "Platforms for artistic creation and exhibition",
      icon: <FaPaintBrush size={40} />,
      activities: ["Photography Club", "Digital Arts Collective", "Campus Mural Project"]
    },
    {
      id: 6,
      title: "Science & Innovation",
      description: "Cutting-edge research and technology groups",
      icon: <MdScience size={40} />,
      activities: ["Robotics Team", "Biotechnology Society", "AI Research Group"]
    }
  ];

  const upcomingEvents = [
    {
      id: 1,
      title: "Tech Symposium 2024",
      date: "15 March 2024",
      location: "Innovation Hall",
      category: "Academic"
    },
    {
      id: 2,
      title: "Cultural Night: Asia Unites",
      date: "22 March 2024",
      location: "Grand Auditorium",
      category: "Cultural"
    },
    {
      id: 3,
      title: "Startup Pitch Competition",
      date: "5 April 2024",
      location: "Business Incubator",
      category: "Entrepreneurship"
    },
    {
      id: 4,
      title: "Science Fair Exhibition",
      date: "12 April 2024",
      location: "Science Complex",
      category: "Science"
    }
  ];

  const highlights = [
    {
      title: "200+ Student Organizations",
      description: "Active clubs catering to diverse interests"
    },
    {
      title: "85% Participation Rate",
      description: "Students engaged in extracurricular activities"
    },
    {
      title: "40 Annual Major Events",
      description: "From academic conferences to cultural festivals"
    }
  ];

  const stats = [
    { value: "150+", label: "Clubs & Organizations" },
    { value: "3000+", label: "Annual Participants" },
    // { value: "40+", label: "Nationalities Represented" },
    // { value: "12", label: "Competition Wins (2023)" }
  ];


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <motion.div 
      className="activities-container"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Hero Section */}
      <div className="activities-hero">
        <motion.div 
          className="hero-content"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>Campus Activities</h1>
          <p className="hero-subtitle-activities">Discover your passion beyond the classroom</p>
          <div className="hero-stats">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                className="stat-card"
                whileHover={{ scale: 1.05 }}
                variants={itemVariants}
              >
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Activity Categories */}
      <section className="categories-section">
        <div className="section-header">
          <h2><FaUsers /> Activity Categories</h2>
          <p>Explore our diverse range of student organizations and clubs</p>
        </div>
        
        <motion.div 
          className="categories-grid"
          variants={containerVariants}
        >
          {activityCategories.map((category) => (
            <motion.div 
              key={category.id}
              className="category-card"
              variants={itemVariants}
              whileHover={{ 
                y: -5,
                boxShadow: "0 10px 30px rgba(0,0,0,0.15)"
              }}
            >
              <div className="category-icon">{category.icon}</div>
              <h3>{category.title}</h3>
              <p>{category.description}</p>
              <div className="activity-list">
                {category.activities.map((activity, index) => (
                  <span key={index} className="activity-tag">{activity}</span>
                ))}
              </div>
              <button className="explore-btn">Explore {category.title}</button>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Upcoming Events */}
      <section className="events-section">
        <div className="section-header">
          <h2><FaCalendarAlt /> Upcoming Events</h2>
          <p>Don't miss these exciting campus activities</p>
        </div>
        
        <motion.div 
          className="events-timeline"
          variants={containerVariants}
        >
          {upcomingEvents.map((event) => (
            <motion.div 
              key={event.id}
              className="event-card"
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
            >
              <div className="event-date">
                <div className="date-day">{event.date.split(' ')[0]}</div>
                <div className="date-month">{event.date.split(' ')[1]}</div>
              </div>
              <div className="event-details">
                <div className="event-category">{event.category}</div>
                <h3>{event.title}</h3>
                <p className="event-location">{event.location}</p>
                <button className="register-btn">Register Now</button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Highlights Section */}
      <section className="highlights-section">
        <motion.div 
          className="highlights-content"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="highlights-text">
            <h2>Why Join Campus Activities?</h2>
            <p>Participation in extracurricular activities enhances your university experience and develops essential life skills valued by employers worldwide.</p>
            <div className="highlight-items">
              {highlights.map((highlight, index) => (
                <div key={index} className="highlight-card">
                  <h3>{highlight.title}</h3>
                  <p>{highlight.description}</p>
                </div>
              ))}
            </div>
            {/* <button className="cta-button">View All Benefits</button> */}
          </div>
          <div className="highlights-image">
            <img src="/activitysing.jpg" alt="Student Activities" />
          </div>
        </motion.div>
      </section>

      {/* Leadership Development */}
      <section className="leadership-section">
        <div className="section-header">
          <h2><MdBusinessCenter /> Leadership Opportunities</h2>
          <p>Develop essential skills through student organization leadership roles</p>
        </div>
        
        <div className="leadership-grid">
          <motion.div 
            className="leadership-card"
            whileHover={{ scale: 1.03 }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <FaUsers size={40} />
            <h3>Organization Leadership</h3>
            <p>President, Vice President, Treasurer and other executive positions in 150+ student organizations</p>
          </motion.div>
          
          <motion.div 
            className="leadership-card"
            whileHover={{ scale: 1.03 }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <FaMicrophone size={40} />
            <h3>Event Management</h3>
            <p>Plan and execute major campus events with budgets up to $50,000</p>
          </motion.div>
          
          <motion.div 
            className="leadership-card"
            whileHover={{ scale: 1.03 }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <FaGlobeAsia size={40} />
            <h3>Global Programs</h3>
            <p>Lead international student initiatives and cultural exchange programs</p>
          </motion.div>
        </div>
      </section>

      {/* Get Involved CTA */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Get Involved?</h2>
          <p>Join our vibrant campus community and make your university experience unforgettable</p>
          <div className="cta-buttons">
            <button className="primary-btn">Browse All Organizations</button>
            <button className="secondary-btn">Start a New Club</button>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Activities;